import React from 'react';
import clsx from 'clsx';

import Infotip from 'App/common/Tooltip/Infotip/infotip';
import { ArticleEvent, Milestone } from 'models/shipments';

import { useCurrentArticle } from '../../hooks/shipment.hooks';
import ScanEvent from '../ScanEvent/ScanEvent';
import {
  CURRENT_MILESTONE_ICON_TEST_ID,
  CURRENT_MILESTONE_NAME_TEST_ID,
  CURRENT_MILESTONE_TEST_ID,
} from './CurrentMilestone.test-ids';

import styles from './CurrentMilestone.module.scss';

interface CurrentMilestoneProps {
  milestone: string;
  events?: ArticleEvent[] | null;
  displayMilestoneTooltip?: boolean;
  displayLocalTimeFootnote?: boolean;
  hasNext?: boolean;
}

const getMilestoneClass = (milestone: string) => {
  switch (milestone) {
    case Milestone.DELIVERED:
      return 'delivered';
    case Milestone.AWAITING_COLLECTION:
      return 'awaitingCollection';
    default:
      return 'default';
  }
};

const CurrentMilestone: React.FC<CurrentMilestoneProps> = ({
  milestone,
  events,
  displayMilestoneTooltip,
  displayLocalTimeFootnote,
  hasNext = false,
}) => {
  const { toolTipText: eventTooltip = '', milestones } = useCurrentArticle() || {};
  const milestoneTooltip = milestones?.[0]?.description || '';
  const milestoneClass = getMilestoneClass(milestone);
  const iconClasses = clsx(styles.currentMilestoneIcon, styles[milestoneClass + 'Icon']);
  const headingClasses = clsx(styles.currentMilestoneHeading, styles[milestoneClass + 'Heading']);
  const tramTrackClasses = clsx(styles.tramTrack, !hasNext && styles.endLine);
  const displayEvents = () =>
    events?.map((e) => (
      <ScanEvent event={e} key={e.dateTime} tooltipText={eventTooltip} displayFootnote={displayLocalTimeFootnote} />
    ));

  return (
    <div className={styles.currentMilestone} data-testid={CURRENT_MILESTONE_TEST_ID}>
      <div className={tramTrackClasses} />
      <div className={styles.currentMilestoneName}>
        <span className={iconClasses} data-testid={CURRENT_MILESTONE_ICON_TEST_ID} />
        <h3 className={headingClasses} data-testid={CURRENT_MILESTONE_NAME_TEST_ID}>
          <span className='sr-only'>Current milestone:&nbsp;</span>
          {milestone}
        </h3>
        {displayMilestoneTooltip && milestoneTooltip && (
          <Infotip anchorClassName={styles.infotip}>{milestoneTooltip}</Infotip>
        )}
      </div>
      <ul className={styles.currentMilestoneEvents}>{displayEvents()}</ul>
    </div>
  );
};

export default CurrentMilestone;
